import { inject, Injectable } from '@angular/core';
import { ICustomTileSectionForm } from '../../entities/custom-tile-section.entity';
import {
  DEFAULT_CUSTOM_TILE_SECTION,
  DEFAULT_CUSTOM_TILE_DIRECT_TICKET,
  ERelationType,
  ICustomTile,
  ICustomTileSection,
} from '@web/dtos';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { FilterTextsPipe } from '../../pipes/filterTexts/filterTexts.pipe';
import { ICustomTileForm } from '../../entities/custom-tile.entity';
import { IAccessibleImageForm } from '../../entities/image-entity';
import { HeaderFormsFactoryService } from '../header/header-forms-factory.service';
import { minDate } from '../../validators/minDate';
import { minDateByControl } from '../../validators/minDateByControl';

@Injectable({
  providedIn: 'root',
})
export class CustomTileSectionFormsFactoryService {
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private readonly filterTexts: FilterTextsPipe = inject(FilterTextsPipe);
  private readonly headerFactory: HeaderFormsFactoryService = inject(
    HeaderFormsFactoryService
  );

  buildForm(
    customTileSection: ICustomTileSection = DEFAULT_CUSTOM_TILE_SECTION
  ): FormGroup<ICustomTileSectionForm> {
    return this.formBuilder.group<ICustomTileSectionForm>({
      id: this.formBuilder.control(customTileSection.id || null),
      position: this.formBuilder.nonNullable.control(
        customTileSection.position
      ),
      texts: this.formBuilder.control(
        this.filterTexts.transform(customTileSection.texts, ERelationType.title)
      ),
      tiles: this.buildTilesFormArray(customTileSection.tiles),
    });
  }

  buildSectionsFormArray(
    sections: ICustomTileSection[]
  ): FormArray<FormGroup<ICustomTileSectionForm>> {
    return this.formBuilder.array<FormGroup<ICustomTileSectionForm>>(
      sections.map((section: ICustomTileSection) => {
        return this.buildForm(section);
      })
    );
  }

  buildTilesFormArray(
    entity: ICustomTile[] = [DEFAULT_CUSTOM_TILE_DIRECT_TICKET]
  ): FormArray<FormGroup<ICustomTileForm>> {
    return this.formBuilder.array<FormGroup<ICustomTileForm>>(
      entity.map((tile: ICustomTile): FormGroup<ICustomTileForm> => {
        return this.buildCustomTileFormGroup(tile);
      })
    );
  }

  buildCustomTileFormGroup(tile: ICustomTile): FormGroup<ICustomTileForm> {
    const startDateValidators: ValidatorFn[] = tile.startDate
      ? [minDate(new Date(tile.startDate))]
      : [minDate(new Date())];
    const form: FormGroup<ICustomTileForm> =
      this.formBuilder.group<ICustomTileForm>({
        id: this.formBuilder.control(tile.id || null),
        isActive: this.formBuilder.nonNullable.control(tile.isActive),
        accessibleImage: this.formBuilder.group<IAccessibleImageForm>({
          id: this.formBuilder.control(tile.accessibleImage?.id ?? null),
          image: this.formBuilder.nonNullable.control(
            tile.accessibleImage?.image ?? null
          ),
          imageAlts: this.formBuilder.nonNullable.control(
            tile.accessibleImage?.imageAlts ?? [],
            []
          ),
          url: this.formBuilder.nonNullable.control(
            tile.accessibleImage?.url ?? null
          ),
          positionOrder: this.formBuilder.control(
            tile.accessibleImage?.positionOrder ?? null,
            []
          ),
        }),
        title: this.formBuilder.nonNullable.control(
          this.filterTexts.transform(tile.texts, ERelationType.title),
          [Validators.required]
        ),
        description: this.formBuilder.nonNullable.control(
          this.filterTexts.transform(tile.texts, ERelationType.descriptionText),
          []
        ),
        linkText: this.formBuilder.nonNullable.control(
          this.filterTexts.transform(tile.texts, ERelationType.iconText)
        ),
        link: this.formBuilder.nonNullable.control(tile?.link ?? '', []),
        startDate: this.formBuilder.control(
          tile?.startDate ?? new Date(),
          startDateValidators
        ),
        endDate: this.formBuilder.control(tile?.endDate ?? null, []),
      });

    form.addValidators(
      minDateByControl(form.controls.endDate, form.controls.startDate)
    );

    return form;
  }

  public buildEntities(
    forms: FormArray<FormGroup<ICustomTileSectionForm>>
  ): ICustomTileSection[] {
    const sections = forms.getRawValue() as unknown as any[];
    const newSections = sections.map((section: ICustomTileSection) => {
      const newSection = { ...section };
      newSection.tiles = section.tiles.map((tile: any): ICustomTile => {
        const newTile = {
          ...tile,
          texts: [...tile.linkText, ...tile.description, ...tile.title],
        };
        delete newTile.linkText;
        delete newTile.title;
        delete newTile.description;
        return newTile;
      });
      return newSection;
    });
    this.headerFactory.removeNullIds(newSections);
    return newSections;
  }
}
